import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { cloneDeep } from "lodash";

import { getAudience, getAudiences, queryKeys } from "@/api";
import { useAudienceContext, useAudienceActions, useDemo } from "@/context";
import { useToken } from "@/hooks/core";
import { useClient } from "@/hooks/useClient";
import { Audience } from "@/types";
import { defaultBuildAudience, defaultSiteAudience, defaultUploadAudience, demoClients, unwrap } from "@/utils";

type AudienceOfType<T> = T extends Audience["audience_type"]
  ? Extract<Audience, { audience_type: T }>
  : Audience | null;

const WRONG_TYPE = "Unable to load audience";

const defaultAudience = (audienceType?: Audience["audience_type"]) =>
  cloneDeep(
    audienceType === "build"
      ? defaultBuildAudience
      : audienceType === "upload"
        ? defaultUploadAudience
        : defaultSiteAudience
  );

/**
 * The audience being viewed or edited. A saved audience is fetched through the
 * cache and loaded into the audience context once, so edits aren't replaced
 * by later fetches; without an id it's a fresh default audience.
 */
export const useAudience = <T extends Audience["audience_type"]>(
  audienceId: string | undefined,
  audienceType?: T,
  segment?: boolean
): {
  audience: AudienceOfType<T>;
  loading: boolean;
  error: string | null;
} => {
  const { currentAudience } = useAudienceContext();
  const { load } = useAudienceActions();
  const getToken = useToken();

  const needsLoad = !!audienceId && currentAudience?.audience_id !== audienceId;

  const { data, error, isFetching } = useQuery({
    queryKey: queryKeys.audienceDetail(audienceId ?? "", audienceType ?? "", !!segment),
    queryFn: async () => unwrap(await getAudience(await getToken(), audienceId!, audienceType ?? "", segment)),
    enabled: needsLoad,
  });

  const wrongType = !!data && !!audienceType && data.audience_type !== audienceType;

  useEffect(() => {
    // Viewing a saved audience: load it once the latest version has arrived
    if (audienceId) {
      if (needsLoad && data && !isFetching && !error && !wrongType) {
        load(data);
      }
      return;
    }

    // Creating a new audience: keep one of the same type already being edited,
    // otherwise start from the default
    if (currentAudience && (!audienceType || currentAudience.audience_type === audienceType)) return;
    load(defaultAudience(audienceType));
  }, [audienceId, audienceType, currentAudience, data, error, isFetching, load, needsLoad, wrongType]);

  const errorMessage = error?.message ?? (wrongType ? WRONG_TYPE : null);

  return {
    audience: (needsLoad ? null : currentAudience) as AudienceOfType<T>,
    loading: needsLoad && !errorMessage,
    error: errorMessage,
  };
};

/** The client's audiences of one type, cached per type and demo client */
export const useAudiences = (audienceType?: string) => {
  const getToken = useToken();
  const client = useClient();
  const { isDemoMode, demoClient } = useDemo();
  const demoLabel = isDemoMode ? demoClients.find(x => x.name === demoClient)?.label : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.audienceList(client, audienceType ?? "", demoLabel),
    queryFn: async () => unwrap(await getAudiences(await getToken(), audienceType ?? "", client, demoLabel)) ?? [],
  });

  return {
    audiences: data ?? null,
    loading: isLoading,
    error: error?.message ?? null,
  };
};
